import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, Output, HostListener, ViewChild, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { PopoverDirective } from 'ngx-bootstrap/popover';

import { TimePickerService } from './services/time-picker.service';

@Component({
    selector: 'app-time-picker',
    templateUrl: './time-picker.component.html',
    styleUrls: ['./time-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TimePickerComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => TimePickerComponent),
            multi: true
        }
    ],
    standalone: false
})
export class TimePickerComponent implements ControlValueAccessor, Validator {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly timePickerService = inject(TimePickerService);
    private readonly translateService = inject(TranslateService);

    @Input()
    public set value(value: string) {
        if (!!value && typeof value !== 'string') {
            throw new Error(`Only string is supported for <app-time-picker />. Got ${JSON.stringify(value)}.`);
        }
        this._value = this.parseTime(value);
        this.updateViewValue();
    }

    @Input()
    public set error(value: string) {
        this.popoverDirective.popover = '';
        this.popoverDirective.placement = this.errorPosition;
        this.popoverDirective.hide();

        if (value) {
            this.popoverDirective.popover = this.translateService.instant(value);
            this.popoverDirective.show();
        }
    }

    @Input() public disable: boolean;
    @Input() public errorPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';

    @Input() public size: 'small' | 'xsmall' = 'small';

    @Output() public change = new EventEmitter<string>();
    @Output() public enterClick = new EventEmitter<void>();

    public get viewValue() {
        return this._viewValue;
    }

    public set viewValue(inputValue: string) {
        this._viewValue = inputValue;
        this._value = this.parseTime(inputValue);
        this._onChange(this._value);
        if (this.lastValueValid) {
            this.change.emit(this._value);
        }
    }

    @ViewChild(PopoverDirective, { static: true }) private readonly popoverDirective: PopoverDirective;
    private _value: string;
    private _viewValue = '';
    private lastValueValid = false;

    @HostListener('keydown', ['$event']) onKeyDown(event: KeyboardEvent) {
        switch (event.key) {
            case 'Enter': {
                this.enterClick.emit();
                break;
            }
            default: {
                this.timePickerService.checkInput(event, this._viewValue);
            }
        }

        event.stopPropagation();
    }

    public registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    public registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    public writeValue(value: string): void {
        this.value = value;
    }

    public onChange(newValue: string): void {
        this.change.emit(newValue);
    }

    public onInputBlur(): void {
        this.updateViewValue();
        this._onTouched();
    }

    public validate() {
        if (!/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/.test(this._value) || !this.lastValueValid) {
            return { timeFormat: true };
        }
        return null;
    }

    private updateViewValue() {
        this._viewValue = this._value;
        this.changeDetectorRef.markForCheck();
    }

    private parseTime(value: string): string {
        if (!value || value.length === 0) {
            this.lastValueValid = true;
            return '';
        }

        if (/^(\d{0,2}):(\d{0,2})$/.test(value)) {
            const separated = value.split(':');
            const hour = separated[0];
            const minute = separated[1].length === 2 ? separated[1] : separated[1].concat('0');
            this.lastValueValid = true;
            return this.timePickerService.formatTime(hour, minute);
        }

        if (/^\d{1,4}$/.test(value)) {
            const hour = value.substr(0, 2);
            const minute = value.substr(2, 2);
            this.lastValueValid = true;
            return this.timePickerService.formatTime(hour, minute);
        }

        this.lastValueValid = false;
        return value;
    }

    private _onChange: any = () => {};
    private _onTouched: any = () => {};
}
